import type { SortDirection, SortKind, SortValue } from "./tableTypes";

function isEmptySortValue(value: SortValue): boolean {
  if (value == null || value === "") {
    return true;
  }
  if (typeof value === "number" && Number.isNaN(value)) {
    return true;
  }
  if (value instanceof Date && Number.isNaN(value.getTime())) {
    return true;
  }
  return false;
}

function toSortNumber(value: SortValue, kind: SortKind): number {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === "number") {
    return value;
  }
  if (kind === "date") {
    const parsed = new Date(String(value)).getTime();
    return Number.isNaN(parsed) ? Number.NaN : parsed;
  }
  return Number(value);
}

/**
 * Compare two sort values.
 * Empty/null/NaN values always sort last, independent of direction.
 */
export function compareSortValues(
  left: SortValue,
  right: SortValue,
  kind: SortKind = "text",
  direction: SortDirection = "asc",
): number {
  const leftEmpty = isEmptySortValue(left);
  const rightEmpty = isEmptySortValue(right);
  if (leftEmpty && rightEmpty) {
    return 0;
  }
  if (leftEmpty) {
    return 1;
  }
  if (rightEmpty) {
    return -1;
  }

  let ordering: number;
  if (kind === "text") {
    ordering = String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  } else {
    const leftNumber = toSortNumber(left, kind);
    const rightNumber = toSortNumber(right, kind);
    if (Number.isNaN(leftNumber) && Number.isNaN(rightNumber)) {
      return 0;
    }
    if (Number.isNaN(leftNumber)) {
      return 1;
    }
    if (Number.isNaN(rightNumber)) {
      return -1;
    }
    ordering = leftNumber - rightNumber;
  }

  return direction === "asc" ? ordering : -ordering;
}

export interface SortableColumnConfig<T, SortKey extends string> {
  sortKey: SortKey;
  sortKind?: SortKind;
  getSortValue: (row: T) => SortValue;
}

export function createColumnCompare<T, SortKey extends string>(
  columns: SortableColumnConfig<T, SortKey>[],
): (
  a: T,
  b: T,
  column: SortKey,
  direction?: SortDirection,
) => number {
  const byKey = new Map<SortKey, SortableColumnConfig<T, SortKey>>();
  for (const column of columns) {
    byKey.set(column.sortKey, column);
  }

  return (a, b, column, direction = "asc") => {
    const config = byKey.get(column);
    if (!config) {
      return 0;
    }
    return compareSortValues(
      config.getSortValue(a),
      config.getSortValue(b),
      config.sortKind ?? "text",
      direction,
    );
  };
}

/** Initial direction when first clicking a column header. */
export function initialSortDirectionForKind(
  kind: SortKind | undefined,
): SortDirection {
  // Dates: newest first. Numbers/text: natural ascending.
  return kind === "date" ? "desc" : "asc";
}
